import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { DatePicker } from "antd";
import { ConfigProvider } from "antd";

import './DatePickerStyle.css'

// El componente ha sido generalizado para manejar cualquier tipo de artículo alquilable.
export default function RentableItemDialog({
  open,
  onClose,
  onSelect,
  itemList, // Prop generalizada
  itemSelectionService,
  itemReservations, // Prop generalizada
  newReservations, // Prop generalizada
  itemType // Nueva prop para personalizar textos ('vestido' o 'traje')
}) {
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [item, setItem] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [datePickerStatus, setDatePickerStatus] = useState('error');
  const [items, setItems] = useState([...itemList]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const range = (start, end) => {
    const result = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  }

  const disabledRangeTime = (_, type) => {
    if (type === 'start') {
      return {
        disabledHours: () => range(0, 24).filter((hour) => hour < 8 || hour > 18)
      };
    } else {
      return {
        disabledHours: () => range(0, 24).filter((hour) => hour < 8 || hour > 20)
      };
    }
  }

  // La lógica de filtrado ahora es genérica para cualquier artículo
  const filteredItems = items.filter((item) => {
    const itemName = item.dressName || item.suitName || '';
    const itemCode = item.code || '';
    return itemName.toLowerCase().includes(search.toLowerCase()) ||
           itemCode.toLowerCase().includes(search.toLowerCase());
  });

  const handleSelect = (item) => {
    const itemId = item.dressID || item.suitID;
    setSelectedItem(itemId);
    setItem(item);
  };

  const handleRangeDate = (dates) => {
    if (dates && dates.length > 0) {
      const rangeDates = dates.map((date) => date.format('YYYY-MM-DD HH:mm:ss'));
      const rangeValue = {
        eventDateStart: rangeDates[0],
        eventDateEnd: rangeDates[1],
      }
      return setDateRange(rangeValue);
    }
    return setDateRange(null);
  };

  // La función de filtrado ahora maneja claves genéricas
  const filterAvailableItems = (list) => {
    const firstFilter = list.filter(item => {
      const itemId = item.dressID || item.suitID;
      return !itemReservations.some(reservation => {
        const reservationStart = new Date(reservation.eventDateStart);
        const reservationEnd = new Date(reservation.eventDateEnd);
        const eventStart = new Date(dateRange.eventDateStart);
        const eventEnd = new Date(dateRange.eventDateEnd);

        const reservationItemId = reservation.dressID || reservation.suitID;
        const isItemReserved = reservationItemId === itemId;
        // Asumimos que los items con ID > 3 son los alquilables
        const isRentableItem = itemId > 3;

        const hasDateConflict = (eventStart >= reservationStart && eventStart <= reservationEnd) ||
          (eventEnd >= reservationStart && eventEnd <= reservationEnd) ||
          (eventStart <= reservationStart && eventEnd >= reservationEnd);

        return isItemReserved && hasDateConflict || !isRentableItem;
      });
    });

    if (newReservations && newReservations.length > 0) {
      return firstFilter.filter(item => {
        const itemId = item.dressID || item.suitID;
        return !newReservations.some(reservation => {
          const reservationStart = new Date(reservation.eventDateStart);
          const reservationEnd = new Date(reservation.eventDateEnd);
          const eventStart = new Date(dateRange.eventDateStart);
          const eventEnd = new Date(dateRange.eventDateEnd);

          const reservationItemId = reservation.dressID || reservation.suitID;
          const isItemReserved = reservationItemId === itemId;
          const isRentableItem = itemId > 3;

          const hasDateConflict = (eventStart >= reservationStart && eventStart <= reservationEnd) ||
            (eventEnd >= reservationStart && eventEnd <= reservationEnd) ||
            (eventStart <= reservationStart && eventEnd >= reservationEnd);

          return isItemReserved && hasDateConflict || !isRentableItem;
        });
      });
    }
    return firstFilter;
  };


  const handleConfirm = () => {
    onSelect(item, itemSelectionService, dateRange);
    onClose();
  };

  const handleCancel = () => {
    onClose();
    setDateRange(null);
  };

  useEffect(() => {
    if (dateRange) {
      if (dateRange.eventDateStart && dateRange.eventDateEnd) {
        setDatePickerStatus('success');
      } else {
        setDatePickerStatus('error');
      }
    } else {
      setDatePickerStatus('error');
    }
  }, [dateRange]);

  useEffect(() => {
    if (dateRange) {
      setItems(filterAvailableItems(itemList));
    } else {
      setItems([...itemList]);
    }
  }, [dateRange, itemList]); // Se agrega itemList como dependencia

  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedItem(null);
      setItem(null);
      setDateRange(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      sx={{ zIndex: 1500, alignItems: 'flex-start !important' }}
    >
      {/* Título dinámico */}
      <DialogTitle>Selector de {itemType}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Selecciona la fecha del alquiler
        </DialogContentText>
        <ConfigProvider theme={{
          components: {
            DatePicker: {
              zIndexPopup: 2000,
            },
          },
        }}>
          <DatePicker.RangePicker
            showTime
            disabledTime={disabledRangeTime}
            onChange={(dates) => handleRangeDate(dates)}
            status={datePickerStatus}
            style={{
              width: '100%',
              height: '40px',
              marginBottom: '1rem',
              right: 0,
            }}
            getPopupContainer={() => document.body}
          />
        </ConfigProvider>
        {
          dateRange && (
            <Grid container spacing={2} sx={{ marginTop: '0.5rem' }}>
              <DialogContentText sx={{ marginLeft: '1rem' }}>
                Seleccione el {itemType} a alquilar
              </DialogContentText>
              <Grid item xs={12}>
                <TextField
                  label={`Buscar ${itemType}`}
                  variant="outlined"
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Grid>
              {
                filteredItems.map((item) => {
                  const itemId = item.dressID || item.suitID;
                  const itemName = item.dressName || item.suitName;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={itemId}>
                      <Card
                        onClick={() => handleSelect(item)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: selectedItem === itemId ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        <CardContent sx={{ height: "90px" }}>
                          <Typography variant="h6">{itemName}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            Código: {item.code}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                })
              }
              {filteredItems.length === 0 && (
                <Grid item xs={12}>
                  <Typography align="center" color="textSecondary" sx={{ mt: 2 }}>
                    No hay {itemType}s disponibles para las fechas seleccionadas.
                  </Typography>
                </Grid>
              )}
            </Grid>
          )
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="error" variant="contained">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          color="success"
          variant="contained"
          disabled={!selectedItem || !dateRange}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}